import {
  Box,
  ButtonBase,
  LinearProgress,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Tier, TierLinePercent } from "../../../services/referrals";

interface TiersCarouselProps {
  tiers: Tier[];
  currentTierCode: string;
  isPremium: boolean;
  referralsTotal: number;
  nextTierTargets?: Record<string, number>;
  onPremiumClick?: () => void;
}

/**
 * Возвращает Line-1 значения (Активация карт / С транзакций), чтобы
 * показать их в мини-плашках внутри карточки тира. Line 1 всегда есть
 * во всех предустановленных тирах.
 */
function line1Rates(t: Tier): TierLinePercent {
  return (
    t.line_percents.find((l) => l.line === 1) ?? {
      line: 1,
      deposit: 0,
      cardIssue: 0,
      transfer: 0,
    }
  );
}

const CardIcon = ({ isPremium }: { isPremium: boolean }) => (
  <Icon
    icon={isPremium ? "solar:crown-bold" : "solar:diamond-bold"}
    width={20}
    height={20}
    color={isPremium ? "#3377FF" : undefined}
  />
);

const StatPill = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      flex: 1,
      borderRadius: "12px",
      padding: "8px 10px",
      backgroundColor: (t) =>
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.03)",
    }}
  >
    <Typography
      sx={{
        fontSize: "10.5px",
        lineHeight: 1.2,
        opacity: 0.55,
        textAlign: "center",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "15px",
        fontWeight: 700,
        textAlign: "center",
        marginTop: "2px",
      }}
    >
      {value}
    </Typography>
  </Box>
);

function TierCard({
  tier,
  isCurrent,
  isLocked,
  isPremium,
  progress,
  progressLabel,
  onPremiumClick,
  theme,
}: {
  tier: Tier;
  isCurrent: boolean;
  isLocked: boolean;
  isPremium: boolean;
  progress: number;
  progressLabel: string;
  onPremiumClick?: () => void;
  theme: Theme;
}) {
  const { t } = useTranslation("referral");
  const line1 = line1Rates(tier);
  const accent = "#3377FF";
  const premiumButton =
    tier.is_premium && !isPremium ? (
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          onPremiumClick?.();
        }}
        sx={{
          borderRadius: "999px",
          padding: "6px 14px",
          backgroundColor: accent,
          color: "#fff",
          fontSize: "13px",
          fontWeight: 600,
          "&:active": { transform: "scale(0.96)" },
        }}
      >
        {t("tiers.join")}
      </ButtonBase>
    ) : null;

  // Плоская карточка тира: тот же secondary.main что и на HeroBalanceCard,
  // тонкая рамка нашим фирменным синим только для текущего/премиум-уровня.
  // Никаких фиолетовых/синих градиентов из старого дизайна.
  const border = isCurrent
    ? `1.5px solid ${accent}`
    : tier.is_premium
      ? "1.5px solid rgba(51, 119, 255, 0.25)"
      : "1.5px solid transparent";

  return (
    <Box
      sx={{
        flex: "0 0 100%",
        scrollSnapAlign: "start",
        paddingRight: "12px",
        paddingTop: "14px",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          position: "relative",
          padding: "14px 16px 16px",
          borderRadius: "20px",
          backgroundColor: "secondary.main",
          color: "text.primary",
          border,
          transition: "border-color 0.2s ease",
        }}
      >
        {isCurrent && (
          <Box
            sx={{
              position: "absolute",
              top: "-13px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: accent,
              color: "#fff",
              fontSize: "11px",
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              zIndex: 2,
              whiteSpace: "nowrap",
            }}
          >
            <Icon icon="solar:check-circle-bold" width={12} height={12} />
            {t("tiers.current_badge")}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CardIcon isPremium={tier.is_premium} />
            <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
              {tier.name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isLocked && !tier.is_premium && (
              <Typography sx={{ fontSize: "12px", opacity: 0.6 }}>
                {progressLabel}
              </Typography>
            )}
            {premiumButton}
          </Box>
        </Box>

        {!tier.is_premium && (
          <LinearProgress
            variant="determinate"
            value={Math.round(progress * 100)}
            sx={{
              marginBottom: "12px",
              height: "4px",
              borderRadius: "2px",
              backgroundColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.05)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: accent,
                borderRadius: "2px",
              },
            }}
          />
        )}

        <Box sx={{ display: "flex", gap: "10px" }}>
          <StatPill
            label={t("tiers.card_issue")}
            value={`${line1.cardIssue}%`}
          />
          <StatPill
            label={t("tiers.from_transactions")}
            value={`${line1.deposit}%`}
          />
        </Box>
      </Box>
    </Box>
  );
}

export function TiersCarousel({
  tiers,
  currentTierCode,
  isPremium,
  referralsTotal,
  onPremiumClick,
}: TiersCarouselProps) {
  const theme = useTheme();
  const { t } = useTranslation("referral");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);

  // Считаем «прогресс» карточки: для текущего и ниже — 100%, для следующего —
  // отношение приглашённых к порогу, для дальнейших — 0.
  const tiersWithProgress = useMemo(() => {
    const sorted = [...tiers].sort((a, b) => a.sort_order - b.sort_order);
    const nonPremium = sorted.filter((t) => !t.is_premium);
    const currentIdx = nonPremium.findIndex((t) => t.code === currentTierCode);
    return sorted.map((t) => {
      if (t.is_premium) {
        return {
          tier: t,
          isCurrent: isPremium && t.code === currentTierCode,
          isLocked: false,
          progress: isPremium ? 1 : 0,
          progressLabel: "",
        };
      }
      const idx = nonPremium.findIndex((x) => x.code === t.code);
      if (idx < 0)
        return {
          tier: t,
          isCurrent: false,
          isLocked: false,
          progress: 0,
          progressLabel: "",
        };
      if (idx <= currentIdx) {
        return {
          tier: t,
          isCurrent: idx === currentIdx,
          isLocked: false,
          progress: 1,
          progressLabel: "",
        };
      }
      // Следующий или дальний
      const isImmediateNext = idx === currentIdx + 1;
      const target = Math.max(1, t.min_referrals);
      const progress = isImmediateNext
        ? Math.min(1, referralsTotal / target)
        : 0;
      return {
        tier: t,
        isCurrent: false,
        isLocked: true,
        progress,
        progressLabel: `${referralsTotal}/${target}`,
      };
    });
  }, [tiers, currentTierCode, isPremium, referralsTotal]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActive(idx);
  }, []);

  useEffect(() => {
    // При первой отрисовке скроллим на текущий тир.
    const el = scrollRef.current;
    if (!el) return;
    const currentIdx = tiersWithProgress.findIndex((x) => x.isCurrent);
    if (currentIdx > 0) {
      el.scrollLeft = currentIdx * el.clientWidth;
      setActive(currentIdx);
    }
  }, [tiersWithProgress]);

  // Мышиный drag-to-scroll: на десктопе overflow-x свайпится только
  // touch/wheel'ом. Добавляем эмуляцию через pointer events, чтобы
  // карусель листалась мышкой — как в Telegram Web.
  const dragState = useRef<{
    isDown: boolean;
    startX: number;
    startScroll: number;
    moved: boolean;
    pointerId: number | null;
  }>({
    isDown: false,
    startX: 0,
    startScroll: 0,
    moved: false,
    pointerId: null,
  });

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Только мышь/pen — тач и так свайпается нативно.
    if (e.pointerType === "touch") return;
    // Не начинаем драг с интерактивных элементов — иначе click по «Вступить»
    // не сработает: capture заберёт pointerup, кнопка его не увидит.
    const target = e.target as HTMLElement | null;
    if (target && target.closest("button, a, [role='button']")) return;
    const el = scrollRef.current;
    if (!el) return;
    dragState.current.isDown = true;
    dragState.current.startX = e.clientX;
    dragState.current.startScroll = el.scrollLeft;
    dragState.current.moved = false;
    dragState.current.pointerId = e.pointerId;
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.isDown) return;
    const dx = e.clientX - dragState.current.startX;
    if (!dragState.current.moved && Math.abs(dx) > 5) {
      dragState.current.moved = true;
      // Только сейчас становимся «активными» — гасим снап и захватываем
      // указатель. Пока пользователь просто держит кнопку — click работает.
      el.style.cursor = "grabbing";
      el.style.scrollBehavior = "auto";
      el.style.scrollSnapType = "none";
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
    if (!dragState.current.moved) return;
    e.preventDefault();
    el.scrollLeft = dragState.current.startScroll - dx;
  }, []);

  const endDrag = useCallback((e?: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    if (!dragState.current.isDown) return;
    const wasMoved = dragState.current.moved;
    dragState.current.isDown = false;
    if (!wasMoved) {
      // Обычный клик — ничего не трогаем, чтобы кнопки внутри карточки
      // работали как обычно.
      dragState.current.pointerId = null;
      return;
    }
    el.style.cursor = "grab";
    el.style.scrollBehavior = "smooth";
    // Довертываем к ближайшей карточке, а затем возвращаем snap.
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    el.scrollTo({ left: idx * el.clientWidth, behavior: "smooth" });
    setActive(idx);
    window.setTimeout(() => {
      if (el) el.style.scrollSnapType = "x mandatory";
    }, 350);
    if (e && dragState.current.pointerId !== null) {
      try {
        el.releasePointerCapture(dragState.current.pointerId);
      } catch {
        /* noop */
      }
    }
    dragState.current.pointerId = null;
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Если пользователь тащил мышку — не даём отработать клику по кнопке
    // «Вступить» и подобным.
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.moved = false;
    }
  }, []);

  return (
    <Box sx={{ marginTop: "16px" }}>
      {/* Хедер секции */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          paddingRight: "4px",
        }}
      >
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {t("tiers.section_title")}
        </Typography>
        <Typography sx={{ fontSize: "11.5px", opacity: 0.55 }}>
          {t("tiers.swipe_hint")}
        </Typography>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          marginRight: "-12px",
          cursor: "grab",
          // pan-x — разрешаем свайп пальцем по горизонтали (иначе телефон
          // блокирует нативный горизонтальный скролл);
          // pan-y — при этом вертикальная прокрутка страницы работает по
          // старту жеста вниз/вверх, чтобы не залипало на карусели.
          // pinch-zoom не нужен, поэтому не добавляем.
          touchAction: "pan-x pan-y",
          userSelect: "none",
          WebkitUserSelect: "none",
          overscrollBehaviorX: "contain",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {tiersWithProgress.map((row) => (
          <TierCard
            key={row.tier.code}
            tier={row.tier}
            isCurrent={row.isCurrent}
            isLocked={row.isLocked}
            isPremium={isPremium}
            progress={row.progress}
            progressLabel={row.progressLabel}
            onPremiumClick={onPremiumClick}
            theme={theme}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "6px",
          marginTop: "10px",
        }}
      >
        {tiersWithProgress.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: i === active ? "18px" : "6px",
              height: "6px",
              borderRadius: "999px",
              backgroundColor:
                i === active
                  ? "primary.main"
                  : theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.15)"
                    : "rgba(0,0,0,0.12)",
              transition: "all 0.2s ease",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
